import React, { useMemo } from 'react';
import ExpandableTooltip from './ExpandableTooltip';
import { IconName } from './Icon/Icon';
import { sanitizeForm } from 'utils/htmlSanitizer';

/**
 * Props for the ExpandableTooltipWrapper component.
 *
 * @property {IconName} [icon] - Optional icon to display within the tooltip.
 * @property {number} [iconSize] - Optional size for the icon.
 * @property {string} childrenHtml - HTML string containing the tooltip content with expand/collapse structure.
 * @property {string} [className] - Optional CSS class for the tooltip container.
 * @property {string} [iconClassName] - Optional CSS class for the icon element.
 */
type ExpandableTooltipWrapperProps = {
    icon?: IconName;
    iconSize?: number;
    childrenHtml: string;
    className?: string;
    iconClassName?: string;
};

/**
 * Parse HTML content and extract introText and expandedContent.
 *
 * @param {string} html - The HTML string to parse.
 * @returns {{ introText: string; expandedContent: React.ReactNode }} Parsed content.
 *
 * @remarks
 * This function extracts text before the `.tooltip-expand` element as introText,
 * and content within `.tooltip-text` as expandedContent.
 * It uses a temporary DOM element for parsing to avoid security issues.
 */
const parseTooltipHtml = (html: string): { introText: string; expandedContent: React.ReactNode } => {
    // Create a temporary element to parse HTML safely
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find the intro text (before the expand link)
    const expandElement = tempDiv.querySelector('.tooltip-expand');
    let introText = '';
    
    if (expandElement) {
        // Get all text nodes before the expand element
        const walker = document.createTreeWalker(
            tempDiv,
            NodeFilter.SHOW_TEXT,
            null
        );
        
        let node = walker.nextNode();
        while (node !== null) {
            if (node.parentElement?.classList.contains('tooltip-expand')) {
                break;
            }
            if (!node.parentElement?.classList.contains('tooltip-text')) {
                introText += node.textContent;
            }
            node = walker.nextNode();
        }
        
        // Remove the expand element from the DOM
        expandElement.remove();
    } else {
        // If no expand element, use all text as intro
        introText = tempDiv.textContent || '';
    }

    // Find the expanded content
    const tooltipTextElement = tempDiv.querySelector('.tooltip-text');
    let expandedContent: React.ReactNode = null;

    if (tooltipTextElement) {
        // Use dangerouslySetInnerHTML for the expanded content (sanitized)
        expandedContent = (
            <div dangerouslySetInnerHTML={{ __html: sanitizeForm(tooltipTextElement.innerHTML) }} />
        );

        // Remove the tooltip-text element from the DOM
        tooltipTextElement.remove();
    }

    // Clean up intro text
    introText = introText.trim();

    return { introText, expandedContent };
};

/**
 * ExpandableTooltipWrapper component wraps ExpandableTooltip to support legacy HTML-based configuration.
 *
 * @remarks
 * - This wrapper parses HTML strings from JSON configuration (childrenHtml prop).
 * - It extracts introText and expandedContent from the HTML structure.
 * - Used for backward compatibility with existing JSON form configurations.
 * - Delegates rendering to ExpandableTooltip for consistent behavior.
 *
 * Migration path:
 * - Use this wrapper when working with existing JSON configurations.
 * - For new implementations, prefer using ExpandableTooltip directly.
 *
 * @param {ExpandableTooltipWrapperProps} props - Props for the wrapper component.
 * @param {string} props.childrenHtml - HTML string with tooltip structure.
 * @param {IconName} [props.icon] - Optional icon name.
 * @param {number} [props.iconSize] - Optional icon size.
 * @param {string} [props.className] - Optional CSS classes.
 * @param {string} [props.iconClassName] - Optional icon CSS classes.
 *
 * @example
 * ```tsx
 * // Used in JSON configuration:
 * {
 *   "component": "ExpandableTooltipWrapper",
 *   "childrenHtml": "Need help? <span class='tooltip-expand'>Expand</span><div class='tooltip-text'>Details here</div>"
 * }
 * ```
 */
const ExpandableTooltipWrapper: React.FC<ExpandableTooltipWrapperProps> = ({
    icon,
    iconSize,
    childrenHtml,
    className,
    iconClassName,
}) => {
    /**
     * Parse the HTML content once and memoize the result.
     * This prevents unnecessary re-parsing on re-renders.
     */
    const { introText, expandedContent } = useMemo(() => {
        return parseTooltipHtml(childrenHtml);
    }, [childrenHtml]);

    // If no intro text or expanded content, fall back to rendering raw HTML
    if (!introText || !expandedContent) {
        return (
            <div className={`bg-light-blue py-10px px-15px radius-5px d-flex align-items-start gap-6px ${className}`}>
                {icon && (
                    <div className={`min-w-24px ${iconClassName}`}>
                        <span>ℹ️</span>
                    </div>
                )}
                <div
                    className="fs-16px text-black"
                    dangerouslySetInnerHTML={{ __html: sanitizeForm(childrenHtml) }}
                />
            </div>
        );
    }

    return (
        <ExpandableTooltip
            icon={icon}
            iconSize={iconSize}
            introText={introText}
            expandedContent={expandedContent}
            className={className}
            iconClassName={iconClassName}
        />
    );
};

export default ExpandableTooltipWrapper;
